import { useState, useEffect } from 'react';
import api from '../lib/api';

/**
 * Hook pour détecter l'activité réseau et la connexion au backend
 * Retourne : { isActive, isConnected, lastActivity }
 */
export const useNetworkActivity = () => {
  const [isActive, setIsActive] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  useEffect(() => {
    let activityTimer = null;

    // Intercepteur de requêtes
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        setIsActive(true);
        setLastActivity(Date.now());
        
        // Désactiver après 300ms
        if (activityTimer) clearTimeout(activityTimer);
        activityTimer = setTimeout(() => setIsActive(false), 300);
        
        return config;
      },
      (error) => {
        setIsActive(false);
        return Promise.reject(error);
      }
    );

    // Intercepteur de réponses
    const responseInterceptor = api.interceptors.response.use(
      (response) => {
        setIsConnected(true);
        return response;
      },
      (error) => {
        // Si erreur réseau, marquer comme déconnecté
        if (!error.response) {
          setIsConnected(false);
        }
        return Promise.reject(error);
      }
    );

    // Vérification périodique de la connexion (toutes les 30s)
    const checkConnection = async () => {
      try {
        await api.get('/health', { timeout: 5000 });
        setIsConnected(true);
      } catch (error) {
        setIsConnected(false);
      }
    };

    // Vérifier immédiatement puis toutes les 30 secondes
    checkConnection();
    const intervalId = setInterval(checkConnection, 30000);

    // Cleanup
    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
      clearInterval(intervalId);
      if (activityTimer) clearTimeout(activityTimer);
    };
  }, []);

  return { isActive, isConnected, lastActivity };
};
